/**
 * The client's only view of the outside world.
 *
 * Everything the app knows about data providers arrives through here, and the
 * component tree never learns a hostname. That mirrors the server's source
 * layer for the same reason: swapping a provider should be one edit in one
 * file, not a hunt through the UI.
 */

/** How well the data chain is currently doing. Mirrors the server's enum. */
export type Health = "full" | "fallback" | "degraded" | "down";

export type Capability = "vector" | "raster" | "elevation";

export interface ProbeResult {
  id: string;
  operator: string;
  capability: Capability;
  reachable: boolean;
  latency_ms: number;
}

export interface HealthReport {
  service: string;
  version: string;
  phase: string;
  uptime_seconds: number;
  database: { status: string; postgis?: string; error?: string };
  basemap: ProbeResult[];
  elevation: ProbeResult[];
  counters: { primary_hits: number; fallback_hits: number; failures: number };
}

export interface ElevationReport {
  lat: number;
  lon: number;
  elevation_m: number;
  resolution_m: number;
  tile: { z: number; x: number; y: number };
  source: string;
  health: Health;
}

export interface BasemapEntry {
  id: string;
  operator: string;
  capability: Capability;
  attribution: string;
  tile_url: string;
}

// The code is a bearer key: once a game is open every game request carries it,
// while public map/source endpoints simply ignore the extra header. Keeping the
// choice here prevents one forgotten fetch from falling back to another game.
let activeGameCode: string | null = null;

export function setGameCode(code: string | null): void {
  activeGameCode = code?.trim().toLowerCase() || null;
}

function requestHeaders(json = false, code = activeGameCode): Record<string, string> {
  const headers: Record<string, string> = {};
  if (json) headers["content-type"] = "application/json";
  if (code) headers["x-game-code"] = code;
  return headers;
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal, headers: requestHeaders() });
  if (!response.ok) {
    // Surface the server's own message when it sent one — it is more specific
    // than anything this layer could invent.
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? `${response.status} ${response.statusText}`);
  }
  return response.json() as Promise<T>;
}

export function fetchHealth(signal?: AbortSignal): Promise<HealthReport> {
  return getJson<HealthReport>("/api/health", signal);
}

export interface AerialConfig {
  tile_url: string;
  /** Licence condition, not a courtesy — must stay visible while shown. */
  attribution: string;
  licence: string;
}

/**
 * The IGM 1:100,000 period map, through the server (the service is HTTP
 * only). Drawn only from `min_zoom`, the scale the service renders at.
 */
export interface HistoricalConfig {
  tile_url: string;
  /** Licence condition, not a courtesy — must stay visible while shown. */
  attribution: string;
  licence: string;
  min_zoom: number;
  max_zoom: number;
}

export interface BasemapConfig {
  sources: BasemapEntry[];
  aerial: AerialConfig;
  historical?: HistoricalConfig;
}

export function fetchBasemaps(signal?: AbortSignal): Promise<BasemapConfig> {
  return getJson<BasemapConfig>("/api/basemap", signal);
}

export function fetchElevation(
  lat: number,
  lon: number,
  zoom: number,
  signal?: AbortSignal,
): Promise<ElevationReport> {
  const q = new URLSearchParams({
    lat: lat.toFixed(6),
    lon: lon.toFixed(6),
    // The elevation dataset stops being useful past z15, and the server clamps
    // anyway; rounding here keeps the request cacheable.
    zoom: String(Math.min(15, Math.max(1, Math.round(zoom)))),
  });
  return getJson<ElevationReport>(`/api/elevation?${q}`, signal);
}

/** Format a latitude/longitude the way a map reader expects to see it. */
export function formatCoord(lat: number, lon: number): string {
  const ns = lat >= 0 ? "N" : "S";
  const ew = lon >= 0 ? "E" : "W";
  return `${Math.abs(lat).toFixed(5)}° ${ns}  ${Math.abs(lon).toFixed(5)}° ${ew}`;
}

// ── Routing ─────────────────────────────────────────────────────────────────

export interface RouteLeg {
  name: string | null;
  class: "motorway" | "primary" | "minor" | "track" | "rail";
  distance_m: number;
  duration_s: number;
}

export interface CrossCountryLeg {
  distance_m: number;
  duration_s: number;
  max_slope_deg: number;
  climbed_m: number;
  points: [number, number][];
}

/** A march that uses roads, possibly with cross-country legs at either end. */
export interface RoadRouteReport {
  mode?: undefined;
  route: {
    distance_m: number;
    duration_s: number;
    points: [number, number][];
    legs: RouteLeg[];
  };
  approach?: CrossCountryLeg;
  departure?: CrossCountryLeg;
  total_distance_m: number;
  total_duration_s: number;
  snapped_from_m: number;
  snapped_to_m: number;
  zoom: number;
  tiles_fetched: number;
  segments: number;
  graph_nodes: number;
}

/** A march made entirely over the ground, because no road connects the points. */
export interface OffRoadReport {
  mode: "cross_country";
  reason: string;
  distance_m: number;
  duration_s: number;
  max_slope_deg: number;
  climbed_m: number;
  points: [number, number][];
}

export type RouteReport = RoadRouteReport | OffRoadReport;

export function fetchRoute(
  from: { lat: number; lon: number },
  to: { lat: number; lon: number },
  signal?: AbortSignal,
): Promise<RouteReport> {
  const q = new URLSearchParams({
    from_lat: from.lat.toFixed(6),
    from_lon: from.lon.toFixed(6),
    to_lat: to.lat.toFixed(6),
    to_lon: to.lon.toFixed(6),
  });
  return getJson<RouteReport>(`/api/route?${q}`, signal);
}

/** Hours and minutes, the way an operations order would state a march time. */
export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}min` : `${m}min`;
}

export function formatDistance(metres: number): string {
  return metres >= 1000 ? `${(metres / 1000).toFixed(1)} km` : `${Math.round(metres)} m`;
}

/** Collapse the leg list into the handful of roads worth naming. */
export function summariseLegs(legs: RouteLeg[], limit = 5): RouteLeg[] {
  const byName = new Map<string, RouteLeg>();
  for (const leg of legs) {
    if (!leg.name) continue;
    const existing = byName.get(leg.name);
    if (existing) {
      existing.distance_m += leg.distance_m;
      existing.duration_s += leg.duration_s;
    } else {
      byName.set(leg.name, { ...leg });
    }
  }
  return [...byName.values()].sort((a, b) => b.distance_m - a.distance_m).slice(0, limit);
}

// ── Game state (phase 2) ────────────────────────────────────────────────────

export type UnitKind = "base" | "scout" | "worker";

export interface Unit {
  id: number;
  kind: UnitKind;
  /** What the formation is, e.g. "reconnaissance" / "company". */
  branch: string;
  echelon: string;
  /** Full MIL-STD-2525 code, built by the server. */
  sidc: string;
  /** True when the viewer owns it — drawn as the friendly frame and fill. */
  mine: boolean;
  /**
   * True when the viewer has *identified* it, not merely seen it.
   *
   * Like `mine`, a property of the viewing relationship rather than of the
   * formation: how well a thing is known depends on who is looking, so it
   * travels beside the symbol rather than in it.
   *
   * Owner, 2026-09-17: an enemy's nation and unit information show when it is
   * within sight — read together with his order of 2026-09-12 that march routes
   * and anything beyond type and level stay hidden. The two only hold as
   * grades: sight gives type and level, identification adds the name,
   * condition, supply and whether it is fighting.
   *
   * **The march route is withheld at every grade**, so `path`, `destination`,
   * `progress` and `eta_s` are null for every enemy however well known.
   * Always true for your own formations, which you do not identify.
   */
  identified: boolean;
  /**
   * Which army it belongs to — `GER` / `ITA` / `USA` / `GBR` / `CAN` — or null
   * for a formation outside the historical order of battle.
   *
   * Drawn as the symbol's fill. Null for an enemy that has been seen but not
   * identified: the server withholds it, because telling *Hermann Göring* from
   * a generic hostile division is what reconnaissance earns.
   */
  nation: string | null;
  /** Base firing range in metres, from the server — drawn as the selection ring. */
  range_m: number;
  /** Indirect-fire reach in metres; 0 for most — drawn as a quiet dashed ring. */
  support_range_m: number;
  /** In a fight right now. */
  engaged: boolean;
  name: string;
  lat: number;
  lon: number;
  /** 0..1 while under orders, null when idle. */
  progress: number | null;
  eta_s: number | null;
  /** Game seconds before a withdrawal has disengaged enough to start moving. */
  starts_in_s: number | null;
  path: [number, number][] | null;
  destination: [number, number] | null;
  /** 1.0 is whole. Below it, the formation has losses to make good. */
  strength: number;
  readiness: number;
  supply: "supplied" | "strained" | "cut" | string;
  combat_stance: "balanced" | "hold_ground" | "assault" | "delay" | string;
  withdrawing: boolean;
  /** Fuel and ammunition carried, 1.0 a full load. Withheld as 1.0 for an
   *  enemy that is only seen. Drawn as the low-supply badge (2026-09-25). */
  fuel: number;
  ammunition: number;
  /** Scheduled theatre entry; absent for the force already on the map. */
  arrival_mode: "air" | "sea" | "mainland" | null;
  /** Wall-clock Unix milliseconds when that durable arrival event fired. */
  arrival_time_ms: number | null;
}

// `Structure` went with the buildings. It was a building that *existed* on the
// map, as opposed to a building kind — a set of rules and a price. Keeping the
// two apart is worth remembering if block D puts fixed installations on the map:
// "what a port is" and "the port at these coordinates" are different objects.

export interface WorldReport {
  id: number;
  game_code: string;
  name: string;
  anchor: [number, number];
  units: Unit[];
  /** (attacker, defender) for every fight the viewer can see both sides of. */
  engagements?: [number, number][];
  /** Theatre clock at the instant this report was taken. */
  game_time_s?: number | null;
  /** Formations booked in the durable theatre queue but not yet ashore. */
  arrivals: ArrivalView[];
  /** Your banked air support (Sicily.md §36) and its ceiling. One point, one strike. */
  air_points?: number | null;
  air_ceiling?: number | null;
  /** Blown bridges not yet repaired, [lat, lon] (Sicily.md §46). */
  demolitions?: [number, number][];
}

export interface ArrivalView {
  event_id: number;
  name: string;
  side: "Allies" | "Axis";
  mode: "air" | "sea" | "mainland";
  at_game_s: number;
  lat: number;
  lon: number;
  opening: boolean;
  /** Entry ground in the order of battle's own words. */
  area: string;
}

export interface MoveReport {
  order_id: number;
  distance_m: number;
  duration_s: number;
  /** How the route was found.
   *
   *  `overland` is the answer of last resort: a straight line at cross-country
   *  pace, with **no terrain read at all** — it does not check water. It exists
   *  because a march anyone can see is possible must not be refused, and it
   *  names itself so the estimate is never mistaken for a planned route. */
  mode: "road" | "cross_country" | "overland";
}

/** One town worth fighting over.
 *
 *  Read out of the `place` layer of the same vector tiles the map is drawn
 *  from, so the campaign is fought over the settlements that are actually
 *  there. An objective cannot be balanced by moving it. */
export interface ObjectiveView {
  id: number;
  name: string;
  class: string;
  weight: number;
  lat: number;
  lon: number;
  holder: number | null;
  /** Whether the holder is you. Sent by the server so the client never has to
   *  work out which nation id is its own. */
  mine: boolean;
  held_since_game_s: number | null;
  /** Husky's decisive ground: scores and carries the phase triggers. The other
   *  70 places only show who holds the ground around them. */
  key_position: boolean;
}

export interface Standing {
  nation_id: number;
  name: string;
  points: number;
  formations: number;
  evacuated: number;
  mine: boolean;
}

/** A phase town or line point, resolved to where this theatre put it. */
export interface PhasePlace {
  name: string;
  lat: number;
  lon: number;
  mine: boolean;
}

/** One of Sicily.md §3's six phases, as the phase panel shows it. */
export interface PhaseView {
  number: number;
  name: string;
  dates: string;
  starts_game_s: number;
  ends_game_s: number;
  allied_goal: string;
  axis_goal: string;
  current: boolean;
  /** When every objective was first held, and the medal that earned. */
  completed_at_game_s: number | null;
  medal: "gold" | "silver" | "bronze" | null;
  objectives: PhasePlace[];
  /** Front, north to south. Empty when the phase has none. */
  line: PhasePlace[];
}

export interface CampaignReport {
  phases?: PhaseView[];
  objectives: ObjectiveView[];
  standings: Standing[];
  /** Points needed to win. Derived from what this theatre holds, so it differs
   *  in every war — never assume a constant. */
  target: number;
  outcome: "points" | "last_standing" | "allied_decisive" | "allied" | "axis_operational" | "axis_major" | null;
  winner_nation_id: number | null;
  finished_at_game_s: number | null;
  game_time_s: number | null;
}

export function fetchCampaign(signal?: AbortSignal): Promise<CampaignReport> {
  return getJson<CampaignReport>("/api/campaign", signal);
}

export function fetchWorld(signal?: AbortSignal): Promise<WorldReport> {
  return getJson<WorldReport>("/api/world", signal);
}

/** Verify a remembered code before mounting the game that will use it. */
export async function continueGame(code: string): Promise<WorldReport> {
  const normalised = code.trim().toLowerCase();
  const response = await fetch("/api/world", {
    headers: requestHeaders(false, normalised),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? `${response.status} ${response.statusText}`);
  }
  return response.json() as Promise<WorldReport>;
}

/** Found a theatre.
 *
 *  `opponent` is sent explicitly rather than left to the server's default. The
 *  default has flipped once already, and a client that says nothing would have
 *  silently changed what founding a theatre means. */
export async function createWorld(
  lat: number,
  lon: number,
  name = "Theatre",
  opponent = true,
): Promise<WorldReport> {
  const response = await fetch("/api/world", {
    method: "POST",
    headers: requestHeaders(true, null),
    body: JSON.stringify({ lat, lon, name, opponent }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? `${response.status} ${response.statusText}`);
  }
  return response.json();
}

/**
 * What the player asked for, when they asked for anything.
 *
 * Owner, 2026-09-15: *"Jag vill införa två typer av förflyttning - via vägar
 * eller via terräng."* Omitting it means "whichever is quicker for this
 * formation", which is what the plain Move order sends.
 */
/** `ground` is the menu's Force move (2026-09-25). `road` is still accepted by
 *  the server but no longer offered: Move takes the road when it is quicker. */
export type MoveMode = "road" | "ground";

export async function orderMove(
  unitId: number,
  lat: number,
  lon: number,
  mode?: MoveMode,
): Promise<MoveReport> {
  const response = await fetch(`/api/units/${unitId}/move`, {
    method: "POST",
    headers: requestHeaders(true),
    body: JSON.stringify(mode ? { lat, lon, mode } : { lat, lon }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? `${response.status} ${response.statusText}`);
  }
  return response.json();
}

// ── Zone of control ─────────────────────────────────────────────────────────

export interface Zone {
  unit_id: number;
  name: string;
  branch: string;
  echelon: string;
  /** The projection budget actually used, after supply and readiness. */
  projection_hours: number;
  /** Ground this formation holds regardless of the terrain around it. */
  min_radius_m: number;
  supply: "supplied" | "strained" | "cut";
  /** Road points that fed the shape. Zero means the floor circle alone. */
  road_points: number;
}

export interface ZocReport {
  world_id: number;
  zones: Zone[];
  /** Your ground, with contested land already removed. */
  area: GeoJSON.Geometry | null;
  /** Theirs, likewise. */
  enemy_area: GeoJSON.Geometry | null;
  /** Where the two meet — a fight is coming, and nothing may be built here. */
  contested_area: GeoJSON.Geometry | null;
  /** Reserved for a future road-supply corridor drawing. Null until the server
   *  can report the same road reach that decides formation supply. */
  detached_area: GeoJSON.Geometry | null;
  /** The reach settings in force. The menu reads these rather than
   *  remembering what it last sent. */
  zoc: ZocSettings;
  note: string | null;
}

export function fetchZoc(signal?: AbortSignal): Promise<ZocReport> {
  return getJson<ZocReport>("/api/zoc", signal);
}

export interface ResetReport {
  worlds_removed: number;
}

/**
 * Delete the game named by the active code. Other players' games are untouched.
 */
export async function resetWorlds(): Promise<ResetReport> {
  const response = await fetch("/api/world", {
    method: "DELETE",
    headers: requestHeaders(),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? `${response.status} ${response.statusText}`);
  }
  return response.json();
}

// ── Clock and log ───────────────────────────────────────────────────────────

/** The theatre clock.
 *
 * ⚠️ This was `EconomyReport` from `/api/economy`, which carried the stockpile,
 * income, upkeep, staff and endurance clock **and** these three fields. The
 * economy went with the RTS layer; the clock only ever rode along on its poll.
 */
export interface ClockReport {
  game_time_s: number;
  paused: boolean;
  /** Game seconds per wall second. Carried here so a reloaded page shows the
   *  speed in force rather than resetting its buttons to 1×. */
  rate: number;
}

export function fetchClock(signal?: AbortSignal): Promise<ClockReport> {
  return getJson<ClockReport>("/api/clock", signal);
}

export interface LogEntry {
  at_game_s: number;
  kind: "arrival" | "construction" | "engagement" | "event";
  text: string;
}

export interface LogReport {
  game_time_s: number;
  entries: LogEntry[];
}

export function fetchLog(signal?: AbortSignal): Promise<LogReport> {
  return getJson<LogReport>("/api/log", signal);
}

export interface BattleSide {
  unit_id: number;
  name: string;
  /** The formation being attacked — the one that names the battle. */
  defending: boolean;
  mine: boolean;
  strength: number;
  /** Null for fights that began before the column existed. */
  strength_at_start: number | null;
  side: "Allies" | "Axis" | string;
  branch: string;
  echelon: string;
  readiness: number;
  supply: string;
  ground: "road" | "open" | "forest" | "urban" | "wetland" | "water";
  elevation_m: number;
  slope_deg: number;
  stance: "balanced" | "hold_ground" | "assault" | "delay";
  combat_power: number;
  factors: { label: string; multiplier: number; explanation: string }[];
}

export interface BattleForce {
  side: "Allies" | "Axis" | string;
  combat_power: number;
  strength_lost: number;
  formations_lost: number;
}

export interface BattleReport {
  /** The same for every participant, which is what makes one fight one report. */
  battle_id: number;
  game_time_s: number;
  hours: number;
  rounds: number;
  momentum: "Allies" | "Axis" | null;
  attackers_lost: number;
  defenders_lost: number;
  sides: BattleSide[];
  forces: BattleForce[];
  /**
   * Nobody is attacking: both sides hold their ground and trade fire lightly
   * until one takes the Assault stance (owner, 2026-09-23).
   */
  standoff: boolean;
}

/**
 * The fight a formation is standing in, or a rejection when it is in none.
 *
 * Asked for *any* hovered formation, friend or enemy: the client does not know
 * whether an enemy is fighting — that was redacted out of the unit feed — so it
 * asks and lets the server decide. The server also refuses a fight the viewer is
 * not part of, so this cannot become a second way to read the other side.
 */
export function fetchBattle(unitId: number, signal?: AbortSignal): Promise<BattleReport> {
  return getJson<BattleReport>(`/api/battle/${unitId}`, signal);
}

export interface ClockReport {
  paused: boolean;
  game_time_s: number;
  mode: "solo" | "contested";
  /** Game seconds per wall second: 1, 10, 25 or 100. */
  rate: number;
  /** Set when the request was refused, with the reason. */
  refused: string | null;
}

/** The speeds the server accepts. Mirrors `clock::RATES`. */
export const RATES = [1, 10, 25, 100] as const;

/**
 * Change how fast the theatre runs.
 *
 * The answer carries the rate that was *applied* — the server snaps to its own
 * allowed set — so callers must render the response, not their own request.
 */
export async function setRate(rate: number): Promise<ClockReport> {
  const response = await fetch("/api/rate", {
    method: "POST",
    headers: requestHeaders(true),
    body: JSON.stringify({ rate }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? `${response.status} ${response.statusText}`);
  }
  return (await response.json()) as ClockReport;
}

/**
 * How a zone of control is computed. Mirrors `zoc::ZocSettings`.
 *
 * Owner, 2026-09-10: a base circle sized by echelon, plus a spread outward
 * whose road and terrain paces are set separately. The paces are for the
 * **zone only** — they never touch how fast a unit actually marches.
 */
export interface ZocSettings {
  /** Radius a company holds regardless of terrain, in metres. */
  base_radius_m: number;
  /** Compounding widening per rung. 0.35 = each step 35% wider. */
  step_per_echelon: number;
  /** How long the theoretical formation is given to spread, in hours. */
  spread_hours: number;
  /** Spread pace along roads, km/h. Zone only. */
  road_kmh: number;
  /** Spread pace across open ground, km/h. Zone only. */
  terrain_kmh: number;
}

export interface SettingsReport {
  zoc: ZocSettings;
}

/**
 * Change one or more reach settings.
 *
 * Fields are optional so a slider sends only what it changed — the client is
 * never made the owner of values it did not touch. The answer carries the
 * settings after clamping, which is what the menu must render: the bounds live
 * on the server and the client holds no copy of them.
 */
export async function setSettings(patch: Partial<ZocSettings>): Promise<SettingsReport> {
  const response = await fetch("/api/settings", {
    method: "POST",
    headers: requestHeaders(true),
    body: JSON.stringify(patch),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? `${response.status} ${response.statusText}`);
  }
  return (await response.json()) as SettingsReport;
}

export async function setPause(paused: boolean): Promise<ClockReport> {
  const response = await fetch("/api/pause", {
    method: "POST",
    headers: requestHeaders(true),
    body: JSON.stringify({ paused }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.error ?? `${response.status} ${response.statusText}`);
  }
  return response.json();
}

// ── Unit actions ────────────────────────────────────────────────────────────

// `BuildingSpec` and `fetchBuildingSpecs` went with `/api/buildings`. The
// catalogue published what each of eleven buildings cost and where it could
// stand, so the client could say where a port may go before the player clicked.
// One building is left and its rules are about an objective ring, not terrain.

export interface ActionResult {
  ok: boolean;
  /** Empty when it went through; otherwise one line per unmet condition. */
  refused: string[];
  id: number | null;
  completes_at_game_s: number | null;
  // `spent` and `remaining` went with the stockpile — nothing an order does
  // costs a resource now.
}

async function act(path: string, body?: unknown): Promise<ActionResult> {
  const response = await fetch(path, {
    method: "POST",
    headers: requestHeaders(body !== undefined),
    body: body ? JSON.stringify(body) : undefined,
  });
  const parsed = await response.json().catch(() => null);
  // A refusal is a 409 carrying reasons, not a failure to be thrown. The whole
  // design is that the game explains itself rather than going quiet.
  if (parsed && typeof parsed.ok === "boolean") return parsed as ActionResult;
  throw new Error(parsed?.error ?? `${response.status} ${response.statusText}`);
}

export const splitUnit = (id: number) => act(`/api/units/${id}/split`);
export const setCombatStance = (id: number, stance: string) =>
  act(`/api/units/${id}/stance`, { stance });
/** Spend one air support point on an enemy formation in sight. */
export const airStrike = (targetId: number) => act(`/api/units/${targetId}/airstrike`, {});
// `repairUnit` went with refit. Strength is permanent now; readiness, fuel and
// ammunition come back on their own while a formation rests in supply, so there
// is no order for a player to give.
export const mergeUnits = (unit_ids: number[]) => act("/api/merge", { unit_ids });
// `unit_id` travels with both: the server decides whether that formation is
// allowed to do this. The client hides the buttons it should not offer, but
// hiding a button is a drawing, not a rule.
// `/api/build` now sets up a supply point and refuses anything else; the shape
// is unchanged so the action path did not have to move. `recruitAt` went with
// `/api/recruit`.
// `buildAt` went with `/api/build`. ⚠️ It outlived the endpoint by a few hours
// and `tsc` said nothing — a client call to a route that answers 404 is a
// perfectly well-typed mistake. **A green type-check is not a correct client.**

// ── THE CENSUS AND THE REFERENCE WENT WITH THE RTS ECONOMY ──────────────────
//
// `/api/census` measured what the ground inside the zone was made of and priced
// it — the engine of an economy where holding ground paid you. `/api/reference`
// listed every building and branch with the numbers that governed them: costs,
// upkeep, recruit costs, yields per ground class.
//
// Husky has no income, nothing to raise and one thing to build, so both answer
// questions the game no longer asks. The principle behind the reference is worth
// carrying into whatever block D publishes about the order of battle: **serve
// the numbers rather than writing them into the client**, because a table kept
// here is a copy that starts accurate and ends wrong.
